import type { Store } from '@blocksuite/store';
import { planRelationColumnDuplicate } from '@streamlain/database-identity';

import type {
  RelationEndpointAccessRequest,
  RelationEndpointAccessResult,
} from './access-state';
import type { RelationConfigSaveResult } from './relation-config-menu';

type RelationDuplicateRefusal =
  | 'target-read-only'
  | 'target-missing'
  | 'target-trashed'
  | 'target-not-loaded';

export type RelationDuplicateOutcome =
  | 'single'
  | 'pair'
  | 'cancelled'
  | RelationDuplicateRefusal
  | 'failed';

/** The part of the database data source a duplicated relation needs. */
export interface RelationDuplicateDataSource {
  readonly doc: Store;
  readonly blockId: string;
  propertyDataGet(propertyId: string): Record<string, unknown>;
  propertyDataSet(propertyId: string, data: Record<string, unknown>): void;
  propertyDuplicate(
    propertyId: string,
    options: { copyValues: false }
  ): string | undefined;
  propertyDelete(propertyId: string): void;
}

/** A backlink column in a target database. */
export interface RelationBacklinkRef {
  targetDocId: string;
  targetDatabaseId: string;
  backlinkId: string;
}

/** What the relation settings of a copy left behind once they closed. */
export interface RelationPairDialogResult {
  /** The backlink the copy was saved with; absent without a saved pair. */
  backlinkId?: string;
  /** Backlinks a save created in a target but never saved into the copy. */
  orphanBacklinks: RelationBacklinkRef[];
}

/**
 * Follows the relation settings of one copy. Closing them while a save runs
 * must not decide the outcome early: the save may still create the backlink
 * or save it into the copy. `closed` therefore waits for every save the
 * settings started, then names the saved backlink and the half-saved ones.
 */
export function trackRelationPairDialog(): {
  saveStarted(result: RelationConfigSaveResult, done: Promise<unknown>): void;
  closed(): Promise<RelationPairDialogResult>;
} {
  const saves: Array<{
    result: RelationConfigSaveResult;
    done: Promise<unknown>;
  }> = [];
  return {
    saveStarted(result, done) {
      saves.push({ result, done });
    },
    async closed() {
      await Promise.allSettled(saves.map(save => save.done));
      const backlinkId = saves.findLast(save => save.result.committedBacklinkId)
        ?.result.committedBacklinkId;
      const orphanBacklinks = new Map<string, RelationBacklinkRef>();
      for (const { result } of saves) {
        const created = result.created;
        if (created && created.backlinkId !== backlinkId)
          orphanBacklinks.set(created.backlinkId, created);
      }
      return { backlinkId, orphanBacklinks: [...orphanBacklinks.values()] };
    },
  };
}

export interface RelationDuplicateDeps {
  resolveAccess(
    request: RelationEndpointAccessRequest
  ): RelationEndpointAccessResult;
  /**
   * Opens the relation settings of the copy, the dialog that creates every
   * relation pair, with the backlink name still editable. Resolves once the
   * settings closed and every save they started has finished.
   */
  configurePair(
    copyId: string,
    originalId: string
  ): Promise<RelationPairDialogResult>;
  removeBacklink(backlink: RelationBacklinkRef): Promise<void>;
  report(
    outcome: RelationDuplicateRefusal | 'failed',
    detail: { database: string; error?: Error }
  ): void;
}

function refusal(
  access: RelationEndpointAccessResult
): RelationDuplicateRefusal | null {
  switch (access.state) {
    case 'write':
      if (!access.database) return 'target-missing';
      return access.database.schemaLoaded === false
        ? 'target-not-loaded'
        : null;
    case 'missing':
      return 'target-missing';
    case 'trashed':
      return 'target-trashed';
    case 'read':
    case 'restricted':
      return 'target-read-only';
  }
}

/**
 * Duplicates a relation column. A relation with a backlink is a pair, so its
 * copy becomes a pair as well (see planRelationColumnDuplicate): the copy is
 * created here without the original's backlink, and the relation settings
 * create its own backlink in the target database. A copy whose settings close
 * without a saved backlink is removed again, and so is every backlink a save
 * created without saving it into the copy: no column stays one-sided.
 *
 * Runs on this device only: the write right on the target comes from the
 * local access state, the target schema from the loaded document.
 */
export async function duplicateRelationColumn(
  dataSource: RelationDuplicateDataSource,
  propertyId: string,
  deps: RelationDuplicateDeps
): Promise<RelationDuplicateOutcome> {
  const plan = planRelationColumnDuplicate(
    dataSource.propertyDataGet(propertyId)
  );
  if (plan.kind === 'single')
    return dataSource.propertyDuplicate(propertyId, { copyValues: false })
      ? 'single'
      : 'failed';

  const access = deps.resolveAccess({
    targetDatabaseId: plan.targetDatabaseId,
    targetDocId: plan.targetDocId ?? dataSource.doc.id,
  });
  const refused = refusal(access);
  if (refused) {
    deps.report(refused, { database: access.databaseTitle });
    return refused;
  }

  // Relation links belong to their pair and are never copied.
  const copyId = dataSource.propertyDuplicate(propertyId, {
    copyValues: false,
  });
  if (!copyId) return 'failed';
  dataSource.propertyDataSet(copyId, plan.copyData);
  const failed = (error: unknown): RelationDuplicateOutcome => {
    deps.report('failed', {
      database: access.databaseTitle,
      error: error instanceof Error ? error : new Error(String(error)),
    });
    return 'failed';
  };
  let dialog: RelationPairDialogResult;
  try {
    dialog = await deps.configurePair(copyId, propertyId);
  } catch (error) {
    dataSource.propertyDelete(copyId);
    return failed(error);
  }
  if (!dialog.backlinkId) dataSource.propertyDelete(copyId);
  try {
    for (const backlink of dialog.orphanBacklinks)
      await deps.removeBacklink(backlink);
  } catch (error) {
    return failed(error);
  }
  return dialog.backlinkId ? 'pair' : 'cancelled';
}
